import { ButtonColor } from "@/common/enum/button-color";
import {
  OrderStatus,
  OrderStatusDisplayNames,
} from "@/common/enum/order-status";
import { AdminQuotationPath } from "@/common/enum/path";
import {
  PaymentStatus,
  paymentStatusDisplayNames,
} from "@/common/enum/payment-status";
import {
  PaymentType,
  paymentTypeDisplayNames,
} from "@/common/enum/payment-type";
import { formatDateToYYYYMMDD } from "@/common/util/date-util";
import { formatCartItemsDisplay } from "@/common/util/format-cart-items-display";
import { StatusButton } from "@/components/button/StatusButton";
import { Body14Regular } from "@/components/typography/Body14Regular";
import { Body14Underline } from "@/components/typography/Body14Underline";
import { Footnote12Description } from "@/components/typography/Footnote12Description";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { Button, Table } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { formatCommasNumber } from "../../../common/util/format-number-with-commas";
import { CustomCell } from "../CustomCell";

interface Props {
  data: OrderDetailDto;
  index: number;
  startIndex: number;
  openPartnerInfoModal: (userId: number) => void;
}

export const AdminOrderRow: React.FC<Props> = ({
  data,
  index,
  startIndex,
  openPartnerInfoModal,
}) => {
  const navigate = useNavigate();
  const item = data;

  const orderStatus = data.status;
  const paymentStatus = data.payment.status;

  const isTaxProcessed =
    item.payment.paymentMethod === PaymentType.PALT &&
    orderStatus === OrderStatus.TAX_PROCESSED;

  const getOrderStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.RECEIVED:
        return ButtonColor.GREEN;
      case OrderStatus.CANCELLED:
        return ButtonColor.RED;
      default:
        return ButtonColor.CUSTOM_GRAY;
    }
  };

  const getPaymentStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.PENDING:
      case PaymentStatus.CANCELED:
      case PaymentStatus.EXPIRED:
        return ButtonColor.RED;
      default:
        return ButtonColor.CUSTOM_GRAY;
    }
  };

  const handleClick = () => {
    navigate(
      `${AdminQuotationPath.ORDER_DETAIL.replace(":id", String(item.id))}`,
    );
  };

  return (
    <Table.Row
      key={item.id}
      className={`${
        item.status === OrderStatus.RECEIVED
          ? "bg-semantic-success-10"
          : "bg-primary-white"
      }`}
    >
      <Table.Cell>
        <CustomCell>
          <Body14Regular className="text-semantic-text-body">
            {startIndex - index}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell onClick={handleClick} className="cursor-pointer">
        <CustomCell>
          <Body14Underline className="text-semantic-text-body">
            {item.invoiceNumber}
          </Body14Underline>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          <Body14Regular className="text-semantic-text-body">
            {formatCartItemsDisplay(item.cartItems)}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          <Body14Regular className="text-semantic-text-body">
            {formatCommasNumber(item.finalPriceSum)}원
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          <Body14Regular className="text-semantic-text-body">
            {formatDateToYYYYMMDD(item.createdAt)}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell
        onClick={() => openPartnerInfoModal(item.ordererId)}
        className="cursor-pointer"
      >
        <CustomCell>
          <Body14Underline className="text-semantic-text-body">
            {item.ordererName}
          </Body14Underline>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          <Body14Regular className="text-semantic-text-body">
            {item.ordererBusinessName}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center justify-center">
          <StatusButton color={getOrderStatusColor(orderStatus)}>
            {OrderStatusDisplayNames[orderStatus]}
          </StatusButton>
        </div>
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center justify-center">
          {isTaxProcessed ? (
            <Button
              color={ButtonColor.WARNING}
              size="xs"
              className="flex justify-center items-center p-0 pointer-events-none cursor-not-allowed"
            >
              <Footnote12Description
                className={"mt-[2px] text-semantic-warning-warning-text"}
              >
                세금계산서 발행 완료
              </Footnote12Description>
            </Button>
          ) : (
            <StatusButton color={getPaymentStatusColor(paymentStatus)}>
              {paymentStatusDisplayNames[paymentStatus]}
            </StatusButton>
          )}
        </div>
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center justify-center">
          {paymentTypeDisplayNames[item.payment.paymentMethod]}
        </div>
      </Table.Cell>
    </Table.Row>
  );
};
